import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ContactCard = ({ 
  contact, 
  onEdit, 
  onMessage, 
  onToggleFavorite,
  isSelected = false,
  onSelect,
  selectionMode = false
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (selectionMode) {
      onSelect(contact?.id);
    } else {
      navigate('/contact-details', { state: { contact } });
    }
  };

  const handleQuickAction = (e, action) => {
    e?.stopPropagation();
    action();
  };

  const getDaysUntilBirthday = (birthday) => {
    if (!birthday) return null;
    
    const today = new Date();
    const birthDate = new Date(birthday);
    const thisYear = today?.getFullYear();
    
    // Set birthday to this year
    const nextBirthday = new Date(thisYear, birthDate.getMonth(), birthDate.getDate());
    
    // If birthday already passed this year, set to next year
    if (nextBirthday < today) {
      nextBirthday?.setFullYear(thisYear + 1);
    }
    
    const diffTime = nextBirthday - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const formatLastContact = (lastContact) => {
    if (!lastContact) return 'Never';
    
    const date = new Date(lastContact);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const daysUntilBirthday = getDaysUntilBirthday(contact?.birthday);
  const isBirthdaySoon = daysUntilBirthday !== null && daysUntilBirthday <= 7;

  return (
    <div 
      className={`bg-surface border border-border rounded-lg p-4 transition-micro hover-lift cursor-pointer ${
        isSelected ? 'ring-2 ring-primary bg-primary/5' : ''
      } ${selectionMode ? 'hover:bg-muted' : ''}`}
      onClick={handleCardClick}
    >
      {/* Selection Checkbox */}
      {selectionMode && (
        <div className="flex items-center mb-3">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
            isSelected ? 'bg-primary border-primary' : 'border-border'
          }`}>
            {isSelected && <Icon name="Check" size={14} color="white" />}
          </div>
        </div>
      )}
      <div className="flex items-start space-x-3">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full overflow-hidden bg-muted">
            {contact?.avatar ? (
              <Image 
                src={contact?.avatar} 
                alt={contact?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name="User" size={24} className="text-text-secondary" />
              </div>
            )}
          </div>
          
          {/* Favorite Badge */}
          {contact?.isFavorite && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-accent rounded-full flex items-center justify-center">
              <Icon name="Star" size={12} color="white" />
            </div>
          )}
          
          {/* Birthday Soon Badge */}
          {isBirthdaySoon && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-warning rounded-full flex items-center justify-center">
              <Icon name="Gift" size={12} color="white" />
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-text-primary truncate">
                {contact?.name}
              </h3>
              
              {contact?.company && (
                <p className="text-sm text-text-secondary truncate">
                  {contact?.company}
                </p>
              )}
              
              <div className="flex items-center space-x-4 mt-2">
                {contact?.phone && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Phone" size={14} className="text-text-secondary" />
                    <span className="text-sm text-text-secondary">
                      {contact?.phone}
                    </span>
                  </div>
                )}
                
                {contact?.email && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Mail" size={14} className="text-text-secondary" />
                    <span className="text-sm text-text-secondary truncate">
                      {contact?.email}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            {!selectionMode && (
              <div className="flex items-center space-x-1 ml-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => handleQuickAction(e, () => onMessage(contact))}
                  className="h-8 w-8"
                >
                  <Icon name="MessageSquare" size={16} />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => handleQuickAction(e, () => onToggleFavorite(contact?.id))}
                  className="h-8 w-8"
                >
                  <Icon 
                    name={contact?.isFavorite ? "Star" : "Star"} 
                    size={16}
                    className={contact?.isFavorite ? "text-accent fill-current" : ""}
                  />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => handleQuickAction(e, () => onEdit(contact))}
                  className="h-8 w-8"
                >
                  <Icon name="Edit" size={16} />
                </Button>
              </div>
            )}
          </div>

          {/* Status Info */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
            <div className="flex items-center space-x-4">
              {daysUntilBirthday !== null && (
                <div className={`flex items-center space-x-1 ${
                  isBirthdaySoon ? 'text-warning' : 'text-text-secondary'
                }`}>
                  <Icon name="Calendar" size={14} />
                  <span className="text-sm">
                    {daysUntilBirthday === 0 ? 'Today!' : 
                     daysUntilBirthday === 1 ? 'Tomorrow' : 
                     `${daysUntilBirthday} days`}
                  </span>
                </div>
              )}
              
              <div className="flex items-center space-x-1 text-text-secondary">
                <Icon name="Clock" size={14} />
                <span className="text-sm">
                  {formatLastContact(contact?.lastContact)}
                </span>
              </div>
            </div>

            {/* Tags */}
            {contact?.tags && contact?.tags?.length > 0 && (
              <div className="flex items-center space-x-1">
                {contact?.tags?.slice(0, 2)?.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                {contact?.tags?.length > 2 && (
                  <span className="text-xs text-text-secondary">
                    +{contact?.tags?.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactCard;